import { FC, useEffect, useState } from "react";

export type WheelEntry = {
  id: string;
  label: string;
  /** ONNINEN one-off: company from `team.onninen.company` — remove with onninen customisation */
  subtitle?: string;
};

type Props = {
  entries: WheelEntry[];
  spinTarget: number | null;
  onSpinComplete: () => void;
};

const PALETTE = ["#ef4444", "#3b82f6", "#10b981", "#f59e0b", "#8b5cf6", "#ec4899"];

const SIZE = 400;
const CENTER = SIZE / 2;
const RADIUS = SIZE / 2 - 12;
const TEXT_RADIUS = RADIUS * 0.62;
const SPIN_DURATION_MS = 5000;
const MIN_FULL_SPINS = 5;

const deg2rad = (d: number) => (d * Math.PI) / 180;

const pointOnCircle = (radius: number, clockwiseFromTopDeg: number): [number, number] => {
  const a = deg2rad(clockwiseFromTopDeg - 90);
  return [CENTER + radius * Math.cos(a), CENTER + radius * Math.sin(a)];
};

const slicePath = (i: number, n: number): string => {
  if (n === 1) {
    return `M ${CENTER} ${CENTER - RADIUS} A ${RADIUS} ${RADIUS} 0 1 1 ${CENTER} ${
      CENTER + RADIUS
    } A ${RADIUS} ${RADIUS} 0 1 1 ${CENTER} ${CENTER - RADIUS} Z`;
  }
  const sliceDeg = 360 / n;
  const [x1, y1] = pointOnCircle(RADIUS, i * sliceDeg);
  const [x2, y2] = pointOnCircle(RADIUS, (i + 1) * sliceDeg);
  const largeArc = sliceDeg > 180 ? 1 : 0;
  return `M ${CENTER} ${CENTER} L ${x1} ${y1} A ${RADIUS} ${RADIUS} 0 ${largeArc} 1 ${x2} ${y2} Z`;
};

const fontSizeFor = (n: number) => (n <= 8 ? 18 : n <= 16 ? 14 : n <= 30 ? 10 : 8);
const maxCharsFor = (n: number) => (n <= 8 ? 18 : n <= 16 ? 12 : n <= 30 ? 8 : 5);

const truncateLabel = (label: string, max: number) =>
  label.length > max ? label.slice(0, Math.max(1, max - 1)) + "…" : label;

export const Wheel: FC<Props> = ({ entries, spinTarget, onSpinComplete }) => {
  const [rotation, setRotation] = useState(0);

  useEffect(() => {
    if (spinTarget === null) return;
    const n = entries.length;
    if (!n) return;

    const sliceDeg = 360 / n;
    const sliceCenterDeg = (spinTarget + 0.5) * sliceDeg;
    const targetMod = (((360 - sliceCenterDeg) % 360) + 360) % 360;

    setRotation((prev) => {
      const currentMod = ((prev % 360) + 360) % 360;
      const delta = (targetMod - currentMod + 360) % 360;
      return prev + MIN_FULL_SPINS * 360 + delta;
    });
  }, [spinTarget]);

  const handleTransitionEnd = (e: React.TransitionEvent) => {
    if (e.propertyName !== "transform") return;
    if (spinTarget === null) return;
    onSpinComplete();
  };

  const n = entries.length;
  const fontSize = fontSizeFor(n);
  const maxChars = maxCharsFor(n);

  return (
    <div className="relative aspect-square w-full max-w-md">
      <svg viewBox={`0 0 ${SIZE} ${SIZE}`} className="h-full w-full">
        <g
          onTransitionEnd={handleTransitionEnd}
          style={{
            transform: `rotate(${rotation}deg)`,
            transformOrigin: `${CENTER}px ${CENTER}px`,
            transition: `transform ${SPIN_DURATION_MS}ms cubic-bezier(0.17, 0.67, 0.21, 0.99)`,
          }}
        >
          {n === 0 ? (
            <circle cx={CENTER} cy={CENTER} r={RADIUS} fill="#e5e7eb" />
          ) : (
            entries.map((entry, i) => {
              const fill = PALETTE[i % PALETTE.length];
              const sliceCenterDeg = (i + 0.5) * (360 / n);
              const svgAngle = sliceCenterDeg - 90;
              return (
                <g key={entry.id}>
                  <path d={slicePath(i, n)} fill={fill} stroke="#fff" strokeWidth={2} />
                  <g transform={`rotate(${svgAngle} ${CENTER} ${CENTER})`}>
                    <text
                      x={CENTER + TEXT_RADIUS}
                      y={CENTER}
                      fill="#fff"
                      fontSize={fontSize}
                      fontWeight={700}
                      textAnchor="middle"
                      dominantBaseline="middle"
                      style={{ pointerEvents: "none", userSelect: "none" }}
                    >
                      {truncateLabel(entry.label, maxChars)}
                    </text>
                  </g>
                </g>
              );
            })
          )}
        </g>
        <circle cx={CENTER} cy={CENTER} r={RADIUS} fill="none" stroke="#1f2937" strokeWidth={4} />
        <polygon
          points={`${CENTER},${CENTER - RADIUS + 6} ${CENTER - 14},${CENTER - RADIUS - 22} ${CENTER + 14},${
            CENTER - RADIUS - 22
          }`}
          fill="#1f2937"
          stroke="#fff"
          strokeWidth={2}
        />
        <circle cx={CENTER} cy={CENTER} r={22} fill="#fff" stroke="#1f2937" strokeWidth={3} />
      </svg>
    </div>
  );
};
